import "@/App.css";
import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import UtmBanner from "@/components/landing/UtmBanner";
import Hero from "@/components/landing/Hero";
import { ProfileSelector, Promises } from "@/components/landing/ProfileSelector";
import ProblemSolution from "@/components/landing/ProblemSolution";
import Features from "@/components/landing/Features";
import DefinitionBlock from "@/components/landing/DefinitionBlock";
import EmergencyCallout from "@/components/landing/EmergencyCallout";
import Segments from "@/components/landing/Segments";
import Results from "@/components/landing/Results";
import AppPreview from "@/components/landing/AppPreview";
import SocialProof from "@/components/landing/SocialProof";
import Pricing from "@/components/landing/Pricing";
import WhyEquiSight from "@/components/landing/WhyEquiSight";
import FinalCTA from "@/components/landing/FinalCTA";
import ContentStats from "@/components/landing/ContentStats";
import Footer from "@/components/landing/Footer";

import SubLanding from "@/pages/SubLanding";
import ArticlePage from "@/pages/ArticlePage";
import HubPage from "@/pages/HubPage";
import BlogHubPage, { categorySlug } from "@/pages/BlogHubPage";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Cookies from "@/pages/Cookies";
import Sitemap from "@/pages/Sitemap";
import ZoekenPage from "@/pages/ZoekenPage";
import WidgetsPage from "@/pages/WidgetsPage";
import TransportDocEmbed from "@/pages/TransportDocEmbed";
import { Support, FaqPage, Downloads, Vergelijkingen } from "@/pages/StaticPages";
import { OverOns, Contact } from "@/pages/Company";

// Lazy-loaded heavy pages (admin + tools + affiliate) — keeps the main bundle
// small for first paint on public pages. Each route gets its own webpack chunk.
const SeoMonitor          = lazy(() => import(/* webpackChunkName: "p-seo"           */ "@/pages/SeoMonitor"));
const BlogGenerator       = lazy(() => import(/* webpackChunkName: "p-blog-gen"      */ "@/pages/BlogGenerator"));
const AdminDashboard      = lazy(() => import(/* webpackChunkName: "p-admin-dash"    */ "@/pages/AdminDashboard"));
const AdminInsights       = lazy(() => import(/* webpackChunkName: "p-admin-ins"     */ "@/pages/AdminInsights"));
const AdminHub            = lazy(() => import(/* webpackChunkName: "p-admin-hub"     */ "@/pages/AdminHub"));
const AppAdmin            = lazy(() => import(/* webpackChunkName: "p-app-admin"     */ "@/pages/AppAdmin"));
const ContentHealth       = lazy(() => import(/* webpackChunkName: "p-content-h"     */ "@/pages/ContentHealth"));
const AdminAffiliate      = lazy(() => import(/* webpackChunkName: "p-admin-aff"     */ "@/pages/AdminAffiliate"));
const AdminResetPassword  = lazy(() => import(/* webpackChunkName: "p-admin-reset"   */ "@/pages/AdminResetPassword"));
const TransportDocLanding = lazy(() => import(/* webpackChunkName: "p-transport-doc" */ "@/pages/TransportDocLanding"));
const AffiliatePartners   = lazy(() => import(/* webpackChunkName: "p-affiliate"     */ "@/pages/AffiliatePartners"));
const AffiliateDashboard  = lazy(() => import(/* webpackChunkName: "p-aff-dash"      */ "@/pages/AffiliateDashboard"));
const Updates             = lazy(() => import(/* webpackChunkName: "p-updates"       */ "@/pages/Updates"));

import AdminAuthGate from "@/components/admin/AdminAuthGate";
import NotFound from "@/pages/NotFound";
import BestePaardenbeheerApp from "@/pages/BestePaardenbeheerApp";
import FokkerijSoftwarePaard from "@/pages/FokkerijSoftwarePaard";
import ManegeSoftware from "@/pages/ManegeSoftware";
import TrainerAppPaard from "@/pages/TrainerAppPaard";
import Gidsen from "@/pages/Gidsen";

import { LANDINGS } from "@/data/landings";
import { getLanding } from "@/data/landingsLocalized";
import { FUNCTIES } from "@/data/functies";
import { DOELGROEPEN } from "@/data/doelgroepen";
import { KENNISBANK } from "@/data/kennisbank";
import { getKennisbank, getKennisbankAsync, isKennisbankFallback } from "@/data/kennisbankLocalized";
import { BLOG } from "@/data/blog";
import { getBlog, getBlogAsync, isBlogFallback } from "@/data/blogLocalized";
import { getCurrentLocale } from "@/i18n/useLocale";
import { PAARDENRASSEN, AANDOENINGEN, ENTINGEN_DETAIL, ONTWORMING_DETAIL, DISCIPLINES } from "@/data/programmatic";
import { SEO } from "@/data/seo";

import Seo from "@/components/Seo";
import { captureUTM, track } from "@/lib/tracking";
import CookieConsent from "@/components/landing/CookieConsent";
import i18n, { LOCALE_CODES, DEFAULT_LOCALE, RTL_LOCALES, localeFromPath } from "@/i18n";

const LocaleSync = () => {
  useEffect(() => {
    // Read locale from full window.location (basename has stripped the prefix
    // from useLocation, so we read pathname directly).
    let target = DEFAULT_LOCALE;
    try {
      const m = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
      if (m && LOCALE_CODES.includes(m[1]) && m[1] !== DEFAULT_LOCALE) {
        target = m[1];
      } else {
        // First-visit auto-detect from navigator language
        const stored = localStorage.getItem("equisight_lng");
        if (stored && LOCALE_CODES.includes(stored)) {
          target = stored;
        } else if (typeof navigator !== "undefined" && navigator.language) {
          const nav = navigator.language.toLowerCase().slice(0, 2);
          if (LOCALE_CODES.includes(nav) && nav !== DEFAULT_LOCALE) {
            target = nav;
          }
        }
      }
    } catch (e) {
      /* ignore */
    }
    if (i18n.language !== target) {
      i18n.changeLanguage(target);
    }
    try {
      document.documentElement.lang = target || DEFAULT_LOCALE;
      document.documentElement.dir = RTL_LOCALES.includes(target) ? "rtl" : "ltr";
    } catch (e) {
      /* ignore */
    }
    // If browser-detected language differs from URL (e.g. user from Germany lands
    // on /, expects /de/), redirect once. Only on the homepage to avoid disrupting
    // deep links.
    if (
      window.location.pathname === "/" &&
      target !== DEFAULT_LOCALE &&
      !localStorage.getItem("equisight_lng_redirected")
    ) {
      try {
        localStorage.setItem("equisight_lng_redirected", "1");
      } catch (e) {
        /* ignore */
      }
      window.location.replace(`/${target}/`);
    }
  }, []);
  return null;
};

const NON_DEFAULT_LOCALES = LOCALE_CODES.filter((c) => c !== DEFAULT_LOCALE);

// Active locale-aware blog dict for routes/listings. Determined ONCE at module
// load — language switches trigger a full page reload (see LanguageSwitcher).
// NL is bundled synchronously; other locales are dynamic-imported on startup
// (see preloadLocaleData below — keeps the main JS bundle ~30 MB lighter).
const ACTIVE_LOCALE = getCurrentLocale();
let ACTIVE_BLOG = getBlog(ACTIVE_LOCALE);
let ACTIVE_KB = getKennisbank(ACTIVE_LOCALE);

/** Promise that resolves when the active locale's blog + KB dictionaries
 *  are loaded. NL resolves immediately; other locales fetch their chunk. */
const localeReady = (async () => {
  if (ACTIVE_LOCALE === DEFAULT_LOCALE) return;
  const [blog, kb] = await Promise.all([
    getBlogAsync(ACTIVE_LOCALE),
    getKennisbankAsync(ACTIVE_LOCALE),
  ]);
  ACTIVE_BLOG = blog;
  ACTIVE_KB = kb;
})();

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    captureUTM();
    track("page_view", { page: location.pathname });
    // Scroll to top on route change, unless navigating to a hash anchor
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [location.pathname, location.hash]);
  return null;
};

const Home = () => {
  return (
    <main data-testid="landing-page" className="bg-background">
      <Seo {...SEO.home} path="/" />
      <Link
        to="/admin"
        data-testid="admin-shortcut-dot"
        aria-label="Admin"
        title="Admin"
        className="fixed left-2 top-2 z-[80] h-2 w-2 rounded-full bg-[hsl(var(--sand))]/60 transition-all hover:bg-[hsl(var(--sand))] hover:scale-150 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sand))]/40 sm:left-3 sm:top-3"
      />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Hero />
      <ProfileSelector />
      <Promises />
      <DefinitionBlock />
      <ProblemSolution />
      <Features />
      <EmergencyCallout />
      <Segments />
      <Results />
      <AppPreview />
      <SocialProof />
      <Pricing />
      <WhyEquiSight />
      <ContentStats />
      <FinalCTA />
      <Footer />
    </main>
  );
};

// Map functie slug -> SEO key
const FUNCTIE_SEO_KEY = {
  wedstrijdanalyse: "wedstrijdanalyse",
  hoefsmid: "hoefsmid",
  dierenarts: "dierenarts",
  trainer: "trainer",
  fokkerij: "fokkerij",
  tandarts: "tandarts",
  manege: "manege",
  voeding: "voeding",
  medicatie: "medicatie",
  agenda: "agenda",
  "protocol-analyse": "protocol-analyse",
  noodnummers: "noodnummers",
  weidegang: "weidegang",
  entingen: "entingen",
  ontwormen: "ontwormen",
  "gewicht-monitoring": "gewicht-monitoring",
};

// Legacy slug -> new /functies path (for 301 redirects)
// Only includes legacy URLs that have a clean direct mapping.
const LEGACY_TO_FUNCTIES = {
  "/wedstrijdanalyse": "/functies/wedstrijdanalyse",
  "/hoefsmid": "/functies/hoefsmid",
  "/trainer": "/functies/trainer",
  "/fokkerij": "/functies/fokkerij",
  "/paardentandarts": "/functies/tandarts",
  "/manege-module": "/functies/manege",
  "/noodnummerpaard": "/functies/noodnummers",
};

const ROUTES = [
  { path: "/eigen-paard", key: "eigen-paard" },
  { path: "/premium", key: "premium" },
  { path: "/manege-ruiter", key: "manege-ruiter" },
  { path: "/manege-module", key: "manege-module" },
  { path: "/fokkerij", key: "fokkerij" },
  { path: "/hoefsmid", key: "hoefsmid" },
  { path: "/paardentandarts", key: "paardentandarts" },
  { path: "/fysio-osteo", key: "fysio-osteo" },
  { path: "/trainer", key: "trainer" },
  { path: "/wedstrijdanalyse", key: "wedstrijdanalyse" },
  { path: "/professional", key: "professional" },
  { path: "/noodnummerpaard", key: "noodnummerpaard" },
];

// Hub items derived from data files
const FUNCTIES_HUB_ITEMS = Object.entries(FUNCTIES).map(([slug, c]) => ({
  slug,
  title: c.title,
  lead: c.subtitle,
  eyebrow: c.eyebrow,
}));
const DOELGROEPEN_HUB_ITEMS = Object.entries(DOELGROEPEN).map(([slug, c]) => ({
  slug,
  title: c.title,
  lead: c.subtitle,
  eyebrow: c.eyebrow,
}));
const KENNISBANK_HUB_ITEMS_GETTER = () => Object.entries(ACTIVE_KB).map(([slug, a]) => ({
  slug,
  title: a.title,
  lead: a.lead,
  eyebrow: a.category,
}));
const BLOG_HUB_ITEMS_GETTER = () => Object.entries(ACTIVE_BLOG)
  .sort((a, b) => (b[1].datePublished ?? "").localeCompare(a[1].datePublished ?? ""))
  .map(([slug, a]) => ({
    slug,
    title: a.title,
    lead: a.lead,
    eyebrow: a.category,
    image: a.heroImage,
  }));

function App() {
  // For non-default locales, wait for the dynamic-imported blog/kb dicts to
  // arrive before rendering routes. NL bundles synchronously → no wait.
  const [localeLoaded, setLocaleLoaded] = useState(ACTIVE_LOCALE === DEFAULT_LOCALE);
  useEffect(() => {
    if (localeLoaded) return;
    localeReady.then(() => setLocaleLoaded(true));
  }, [localeLoaded]);

  // Compute hub items lazily (after locale data has arrived)
  const KENNISBANK_HUB_ITEMS = useMemo(KENNISBANK_HUB_ITEMS_GETTER, [localeLoaded]);
  const BLOG_HUB_ITEMS = useMemo(BLOG_HUB_ITEMS_GETTER, [localeLoaded]);
  // Detect locale from URL on initial load; set BrowserRouter basename accordingly.
  // Language switches trigger a full page reload (via window.location), so basename
  // is always in sync with the URL.
  let basename = "/";
  try {
    const m = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
    if (m && LOCALE_CODES.includes(m[1]) && m[1] !== DEFAULT_LOCALE) {
      basename = `/${m[1]}`;
    }
  } catch (e) {
    /* SSR */
  }
  return (
    <HelmetProvider>
      <div className="App">
        <Toaster position="top-center" richColors closeButton />
        <BrowserRouter basename={basename}>
          <LocaleSync />
          <RouteTracker />
          <CookieConsent />
          <Suspense fallback={
            <div className="min-h-[60vh] flex items-center justify-center text-[12px] text-[hsl(var(--ink-soft))]">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[hsl(var(--forest))]" />
              <span className="ml-2">Laden…</span>
            </div>
          }>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/voorwaarden" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/zoeken" element={<ZoekenPage />} />
            <Route path="/widgets" element={<WidgetsPage />} />
            <Route path="/embed/transport-doc" element={<TransportDocEmbed />} />

            {/* Support / FAQ / Downloads / Blog / Vergelijkingen / About / Contact */}
            <Route path="/support" element={<Support />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/downloads" element={<Downloads />} />
            <Route
              path="/blog"
              element={
                <BlogHubPage
                  seo={{
                    title: "Blog — paardenkennis & EquiSight nieuws",
                    description:
                      "150+ artikelen over paardenbeheer, gezondheid, voeding, training, wedstrijden en kostenoverzichten. Filter op categorie en lees wat bij jou past.",
                    keywords: ["paarden blog", "paardenkennis blog", "equisight blog"],
                  }}
                  path="/blog"
                  eyebrow="Blog"
                  title="Paardenkennis, praktisch toepasbaar."
                  intro="Van kostenoverzichten tot eerste hulp, van hoefverzorging tot fokkerij — 150+ artikelen, gegroepeerd per categorie zodat je snel vindt wat je zoekt."
                  items={BLOG_HUB_ITEMS}
                  itemBasePath="/blog"
                  breadcrumbs={[{ name: "Blog", path: "/blog" }]}
                />
              }
            />
            {/* Per-category SEO landing pages */}
            {Array.from(new Set(Object.values(ACTIVE_BLOG).map((b) => b.category))).map((cat) => {
              const slug = categorySlug(cat);
              const count = BLOG_HUB_ITEMS.filter((it) => it.eyebrow === cat).length;
              return (
                <Route
                  key={`blog-cat-${slug}`}
                  path={`/blog/categorie/${slug}`}
                  element={
                    <BlogHubPage
                      seo={{
                        title: `${cat} — paarden blog | EquiSight`,
                        description: `${count} artikelen over ${cat.toLowerCase()} bij paarden. Praktische kennis, geschreven voor paardeneigenaren, ruiters en stalprofessionals.`,
                        keywords: [
                          cat.toLowerCase(),
                          `paarden ${cat.toLowerCase()}`,
                          "equisight blog",
                        ],
                      }}
                      path={`/blog/categorie/${slug}`}
                      eyebrow={cat}
                      title={`Alles over ${cat.toLowerCase()}.`}
                      intro={`${count} artikelen gefocust op ${cat.toLowerCase()} — praktisch, met concrete getallen en bronnen.`}
                      items={BLOG_HUB_ITEMS}
                      itemBasePath="/blog"
                      breadcrumbs={[
                        { name: "Blog", path: "/blog" },
                        { name: cat, path: `/blog/categorie/${slug}` },
                      ]}
                      activeCategory={slug}
                    />
                  }
                />
              );
            })}
            {Object.entries(ACTIVE_BLOG).map(([slug, article]) => {
              // If this blog is part of a cornerstone topic cluster, insert the
              // cornerstone into the breadcrumb trail for stronger topical
              // hierarchy signals (BreadcrumbList schema + visual context).
              const cs = article.cornerstone_link;
              const blogCrumbs = cs
                ? [
                    { name: "Blog", path: "/blog" },
                    { name: cs.label, path: cs.path },
                    { name: article.title, path: `/blog/${slug}` },
                  ]
                : [
                    { name: "Blog", path: "/blog" },
                    { name: article.title, path: `/blog/${slug}` },
                  ];
              return (
                <Route
                  key={`blog-${slug}`}
                  path={`/blog/${slug}`}
                  element={
                    <ArticlePage
                      article={article}
                      seo={{
                        title: `${article.title} | EquiSight blog`,
                        description: article.lead,
                        keywords: [article.category.toLowerCase(), "paard", "equisight"],
                      }}
                      basePath="/blog"
                      relatedAll={ACTIVE_BLOG}
                      breadcrumbs={blogCrumbs}
                      isFallback={isBlogFallback(slug, ACTIVE_LOCALE)}
                    />
                  }
                />
              );
            })}
            <Route path="/vergelijkingen" element={<Vergelijkingen />} />
            <Route path="/beste-paardenbeheer-app" element={<BestePaardenbeheerApp />} />
            <Route path="/fokkerij-software-paard" element={<FokkerijSoftwarePaard />} />
            <Route path="/manege-software" element={<ManegeSoftware />} />
            <Route path="/trainer-app-paard" element={<TrainerAppPaard />} />
            <Route path="/gidsen" element={<Gidsen />} />
            <Route path="/over-ons" element={<OverOns />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin" element={<AdminAuthGate><AdminHub /></AdminAuthGate>} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/affiliate" element={<AdminAuthGate><AdminAffiliate /></AdminAuthGate>} />
            <Route path="/admin/insights" element={<AdminAuthGate><AdminInsights /></AdminAuthGate>} />
            <Route path="/admin/app" element={<AdminAuthGate><AppAdmin /></AdminAuthGate>} />
            <Route path="/admin/leads" element={<AdminAuthGate><AdminDashboard /></AdminAuthGate>} />
            <Route path="/admin/seo" element={<AdminAuthGate><SeoMonitor /></AdminAuthGate>} />
            <Route path="/admin/blog-generator" element={<AdminAuthGate><BlogGenerator /></AdminAuthGate>} />
            <Route path="/admin/content-health" element={<AdminAuthGate><ContentHealth /></AdminAuthGate>} />
            <Route path="/tools/vervoersdocument-paard" element={<TransportDocLanding />} />
            <Route path="/tools/horse-transport-document" element={<TransportDocLanding />} />

            {/* Affiliate program */}
            <Route path="/affiliate" element={<AffiliatePartners />} />
            <Route path="/partners" element={<Navigate to="/affiliate" replace />} />
            <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />

            {/* Public changelog / what's new */}
            <Route path="/updates" element={<Updates />} />
            <Route path="/wat-is-nieuw" element={<Navigate to="/updates" replace />} />
            <Route path="/changelog" element={<Navigate to="/updates" replace />} />

            {/* Functies hub + sub-pages */}
            <Route
              path="/functies"
              element={
                <HubPage
                  seo={SEO.functiesIndex}
                  path="/functies"
                  eyebrow="Functies"
                  title="Alles wat EquiSight kan."
                  intro="Van wedstrijdanalyse en gezondheidsdossier tot hoefsmid log, ontworming, entingen en agenda — bekijk hieronder per functie."
                  items={FUNCTIES_HUB_ITEMS}
                  itemBasePath="/functies"
                  breadcrumbs={[{ name: "Functies", path: "/functies" }]}
                />
              }
            />
            {Object.entries(FUNCTIES).map(([slug, config]) => (
              <Route
                key={`functies-${slug}`}
                path={`/functies/${slug}`}
                element={
                  <SubLanding
                    config={config}
                    seo={SEO.functies[FUNCTIE_SEO_KEY[slug]] ?? { title: config.title, description: config.subtitle }}
                    path={`/functies/${slug}`}
                    breadcrumbs={[
                      { name: "Functies", path: "/functies" },
                      { name: config.eyebrow?.replace(/^Functie · /, "") ?? slug, path: `/functies/${slug}` },
                    ]}
                  />
                }
              />
            ))}

            {/* Doelgroepen hub + sub-pages */}
            <Route
              path="/doelgroepen"
              element={
                <HubPage
                  seo={SEO.doelgroepenIndex}
                  path="/doelgroepen"
                  eyebrow="Doelgroepen"
                  title="Voor wie is EquiSight?"
                  intro="EquiSight is gemaakt voor iedereen rond het paard. Bekijk hieronder welke doelgroep bij jou past."
                  items={DOELGROEPEN_HUB_ITEMS}
                  itemBasePath="/doelgroepen"
                  breadcrumbs={[{ name: "Doelgroepen", path: "/doelgroepen" }]}
                />
              }
            />
            {Object.entries(DOELGROEPEN).map(([slug, config]) => (
              <Route
                key={`doelgroepen-${slug}`}
                path={`/doelgroepen/${slug}`}
                element={
                  <SubLanding
                    config={config}
                    seo={SEO.doelgroepen[slug] ?? { title: config.title, description: config.subtitle }}
                    path={`/doelgroepen/${slug}`}
                    breadcrumbs={[
                      { name: "Doelgroepen", path: "/doelgroepen" },
                      { name: config.eyebrow?.replace(/^Doelgroep · /, "") ?? slug, path: `/doelgroepen/${slug}` },
                    ]}
                  />
                }
              />
            ))}

            {/* Kennisbank hub + articles */}
            <Route
              path="/kennisbank"
              element={
                <HubPage
                  seo={SEO.kennisbankIndex}
                  path="/kennisbank"
                  eyebrow="Kennisbank"
                  title="Alles over paarden — overzichtelijk uitgelegd."
                  intro="Heldere artikelen over gezondheid, voeding, training, entingen, koliek en meer. Informatief — geen veterinair advies."
                  items={KENNISBANK_HUB_ITEMS}
                  itemBasePath="/kennisbank"
                  breadcrumbs={[{ name: "Kennisbank", path: "/kennisbank" }]}
                />
              }
            />
            {Object.entries(ACTIVE_KB).map(([slug, article]) => (
              <Route
                key={`kb-${slug}`}
                path={`/kennisbank/${slug}`}
                element={
                  <ArticlePage
                    article={article}
                    seo={SEO.kennisbank[slug]}
                    basePath="/kennisbank"
                    relatedAll={ACTIVE_KB}
                    breadcrumbs={[
                      { name: "Kennisbank", path: "/kennisbank" },
                      { name: article.title, path: `/kennisbank/${slug}` },
                    ]}
                    isFallback={isKennisbankFallback(slug, ACTIVE_LOCALE)}
                  />
                }
              />
            ))}

            {/* Programmatic SEO clusters */}
            {Object.entries(PAARDENRASSEN).map(([slug, article]) => (
              <Route
                key={`pr-${slug}`}
                path={`/paardenrassen/${slug}`}
                element={
                  <ArticlePage
                    article={article}
                    seo={SEO.paardenrassen[slug]}
                    basePath="/paardenrassen"
                    relatedAll={PAARDENRASSEN}
                    breadcrumbs={[
                      { name: "Paardenrassen", path: "/paardenrassen" },
                      { name: article.title, path: `/paardenrassen/${slug}` },
                    ]}
                  />
                }
              />
            ))}
            {Object.entries(AANDOENINGEN).map(([slug, article]) => (
              <Route
                key={`aa-${slug}`}
                path={`/aandoeningen/${slug}`}
                element={
                  <ArticlePage
                    article={article}
                    seo={SEO.aandoeningen[slug]}
                    basePath="/aandoeningen"
                    relatedAll={AANDOENINGEN}
                    breadcrumbs={[
                      { name: "Aandoeningen", path: "/aandoeningen" },
                      { name: article.title, path: `/aandoeningen/${slug}` },
                    ]}
                  />
                }
              />
            ))}
            {Object.entries(ENTINGEN_DETAIL).map(([slug, article]) => (
              <Route
                key={`en-${slug}`}
                path={`/entingen/${slug}`}
                element={
                  <ArticlePage
                    article={article}
                    seo={SEO.entingen_routes[slug]}
                    basePath="/entingen"
                    relatedAll={ENTINGEN_DETAIL}
                    breadcrumbs={[
                      { name: "Entingen", path: "/entingen" },
                      { name: article.title, path: `/entingen/${slug}` },
                    ]}
                  />
                }
              />
            ))}
            {Object.entries(ONTWORMING_DETAIL).map(([slug, article]) => (
              <Route
                key={`ow-${slug}`}
                path={`/ontworming/${slug}`}
                element={
                  <ArticlePage
                    article={article}
                    seo={SEO.ontworming[slug]}
                    basePath="/ontworming"
                    relatedAll={ONTWORMING_DETAIL}
                    breadcrumbs={[
                      { name: "Ontworming", path: "/ontworming" },
                      { name: article.title, path: `/ontworming/${slug}` },
                    ]}
                  />
                }
              />
            ))}
            {Object.entries(DISCIPLINES).map(([slug, article]) => (
              <Route
                key={`di-${slug}`}
                path={`/disciplines/${slug}`}
                element={
                  <ArticlePage
                    article={article}
                    seo={SEO.disciplines[slug]}
                    basePath="/disciplines"
                    relatedAll={DISCIPLINES}
                    breadcrumbs={[
                      { name: "Disciplines", path: "/disciplines" },
                      { name: article.title, path: `/disciplines/${slug}` },
                    ]}
                  />
                }
              />
            ))}

            {/* Legacy URLs → 301 redirects to new structure */}
            {Object.entries(LEGACY_TO_FUNCTIES).map(([from, to]) => (
              <Route key={`legacy-${from}`} path={from} element={<Navigate to={to} replace />} />
            ))}

            {/* Legacy routes (kept for direct access if still desired) — render with SEO */}
            {ROUTES.map((r) => {
              if (LEGACY_TO_FUNCTIES[r.path]) return null;
              return (
                <Route
                  key={r.path}
                  path={r.path}
                  element={<SubLanding config={getLanding(r.key, ACTIVE_LOCALE)} path={r.path} />}
                />
              );
            })}

            {/* Catch-all 404 — must be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
